'use client';

import { Box, Button, Center, Flex, Text } from '@chakra-ui/react';
import { FileText, MessageSquare, Monitor, Palette, SwatchBook } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useLivingDocsPanel, useLivingDocsPanelClose } from '@/contexts/LivingDocsPanelContext';
import { SidePanelResizeHandle, useSidePanelSize } from './SidePanelSize';
import { SidePanelToggleButton } from './SidePanelToggleButton';

export function SidePanelLauncher() {
  const { open, openToolChooser, requestClosePanel, panelHasCloseControl } = useLivingDocsPanel();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(open);

  useEffect(() => {
    if (wasOpenRef.current && !open) buttonRef.current?.focus();
    wasOpenRef.current = open;
  }, [open]);

  if (open && panelHasCloseControl) return null;

  return (
    <SidePanelToggleButton
      ref={buttonRef}
      open={open}
      desktopOnly
      onClick={open ? requestClosePanel : openToolChooser}
    />
  );
}

interface SidePanelToolsProps {
  onClose: () => void;
  onOpenDocs: () => void;
  onOpenComments: () => void;
  onOpenCanvas?: () => void;
  onOpenDesignSystem?: () => void;
  onOpenWebsitePreview?: () => void;
}

export function SidePanelTools({
  onClose,
  onOpenDocs,
  onOpenComments,
  onOpenCanvas,
  onOpenDesignSystem,
  onOpenWebsitePreview,
}: SidePanelToolsProps) {
  useLivingDocsPanelClose(onClose, true);
  const { panelWidthValue } = useSidePanelSize();
  const firstToolRef = useRef<HTMLButtonElement>(null);
  useEffect(() => firstToolRef.current?.focus(), []);
  const tools = [
    { label: 'Docs & Assets', icon: FileText, onClick: onOpenDocs },
    { label: 'Comments', icon: MessageSquare, onClick: onOpenComments },
    { label: 'Canvas', icon: Palette, onClick: onOpenCanvas },
    { label: 'DESIGN.md', icon: SwatchBook, onClick: onOpenDesignSystem },
    { label: 'Website preview', icon: Monitor, onClick: onOpenWebsitePreview },
  ].filter(tool => tool.onClick);

  return (
    <Flex
      as="aside"
      aria-label="Side panel tools"
      data-section="sidePanelTools"
      direction="column"
      position="relative"
      w={panelWidthValue}
      minW={panelWidthValue}
      flexShrink={0}
      h="full"
      minH={0}
      borderLeftWidth="1px"
      borderColor="ui.border.subtle"
      bg="ui.surface.rail"
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
      }}
    >
      <SidePanelResizeHandle label="Resize side panel" />
      <Flex h="54px" px="3" align="center" justify="space-between" flexShrink={0}>
        <Text fontSize="13px" color="ui.text.secondary">
          Choose a tool
        </Text>
        <SidePanelToggleButton open onClick={onClose} />
      </Flex>
      <Center flex="1" minH={0} overflowY="auto" px={{ base: '4', xl: '10' }} py="8">
        <Flex direction="column" gap="2" w="full" maxW="380px">
          {tools.map((tool, index) => (
            <Button
              key={tool.label}
              ref={index === 0 ? firstToolRef : undefined}
              variant="ghost"
              justifyContent="flex-start"
              gap="3"
              h="48px"
              px="4"
              borderRadius="10px"
              fontSize="14px"
              fontWeight="500"
              color="ui.text.primary"
              _icon={{ boxSize: '18px' }}
              bg="ui.surface.panel"
              _hover={{ bg: 'ui.surface.input' }}
              _focusVisible={{
                outline: '2px solid',
                outlineColor: 'accent.focusRing',
                outlineOffset: '2px',
              }}
              onClick={tool.onClick}
            >
              <Box as="span" color="ui.text.secondary">
                <tool.icon size={18} />
              </Box>
              {tool.label}
            </Button>
          ))}
        </Flex>
      </Center>
    </Flex>
  );
}
